import { ref } from 'vue'
import type { Confirmation, EmailPreview, PoStatus, ReleaseUpdate } from '../types'

const API = `${import.meta.env.VITE_API_BASE_URL || ''}/api/po-confirmations`

// Module-level singletons: load once, persist across navigation.
const confirmations = ref<Confirmation[]>([])
const status = ref<PoStatus | null>(null)
const loading = ref(false)
const uploading = ref(false)
const applying = ref(false)
const error = ref<string | null>(null)

let loaded = false

const LP = '[po-confirmations]'

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((body as any).error || `${res.status} ${res.statusText}`)
  return body as T
}

/** Print the check outcome to the browser console so an empty Epicor column is
 *  debuggable from DevTools alone (F12 → Console). */
function logConfirmation(c: Confirmation, source: string) {
  try {
    console.groupCollapsed(`${LP} ${source}: PO ${c.poNum ?? '?'} — ${c.filename} — status=${c.status}`)
    if (c.checkError) console.warn(`${LP} checkError:`, c.checkError)
    if (!c.report) {
      console.warn(`${LP} no Epicor report on this confirmation — the check either did not run (no MULESOFT_BASE_URL) or failed. Epicor columns will be blank.`)
    } else {
      const r = c.report as any
      console.log(`${LP} Epicor report:`, r)
      if (!Array.isArray(r.lines)) {
        console.warn(`${LP} report has NO 'lines' array — top-level keys:`, Object.keys(r),
          `. The UI reads report.lines[].checks.{quantity,unitPrice,extPrice,shipVia}. If the real endpoint uses different names, that is why Epicor is blank.`)
      } else {
        console.table(r.lines.map((l: any) => ({
          poLine: l.poLine, matched: l.matched,
          poQty: l.checks?.quantity?.poQty ?? null,
          qtyStatus: l.checks?.quantity?.status ?? null,
          poPrice: l.checks?.unitPrice?.poPrice ?? null,
          poShipVia: l.checks?.shipVia?.poShipVia ?? null,
          action: l.releaseAction ?? null,
        })))
        const unmatched = r.lines.filter((l: any) => l.matched === false).map((l: any) => l.poLine)
        if (unmatched.length) {
          console.warn(`${LP} lines NOT matched on the PO: ${unmatched.join(', ')} — Epicor values come back null for these.`,
            `Likely the sent poLine (a best-guess from confirmation order) is not the real Epicor line for that part. Fix: match by part number.`)
        }
      }
    }
    console.log(`${LP} extracted (from the PDF):`, c.extracted)
    console.groupEnd()
  } catch { /* logging must never break the UI */ }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onerror = () => reject(new Error('Could not read file'))
    r.onload = () => resolve(String(r.result).split(',')[1] || '')
    r.readAsDataURL(file)
  })
}

function upsert(c: Confirmation) {
  const i = confirmations.value.findIndex((x) => x.id === c.id)
  if (i >= 0) confirmations.value[i] = c
  else confirmations.value = [c, ...confirmations.value]
}

/**
 * The email behind a record, and the PDF bytes for the preview iframe. Both go
 * through the same `/api/po-confirmations` path (and so the same auth) as the
 * rest of the module; the PDF becomes an object URL because an <iframe src>
 * cannot carry the request itself. The caller owns the URL and must revoke it.
 */
async function fetchEmailPreview(id: string): Promise<EmailPreview> {
  return json<EmailPreview>(`/${encodeURIComponent(id)}/email`)
}

async function fetchPdfObjectUrl(id: string): Promise<string> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}/pdf`)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as any).error || `${res.status} ${res.statusText}`)
  }
  return URL.createObjectURL(await res.blob())
}

export function usePoConfirmations() {
  async function load(force = false) {
    if (loaded && !force) return
    loading.value = true
    error.value = null
    try {
      const [s, list] = await Promise.all([
        json<PoStatus>('/status'),
        json<{ confirmations: Confirmation[] }>('/'),
      ])
      status.value = s
      confirmations.value = list.confirmations
      loaded = true
      console.log(`${LP} status:`, s)
      list.confirmations.forEach((c) => logConfirmation(c, 'load'))
    } catch (e: any) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  /** Re-read the record list without the per-record console dump — the sidebar badge's poll
   *  (attention.ts). Leaves `loaded` alone so the page's first open still fetches the status too;
   *  a failed poll keeps the last list (the page reports its own errors). */
  async function refreshQuiet(): Promise<void> {
    try {
      const list = await json<{ confirmations: Confirmation[] }>('/')
      confirmations.value = list.confirmations
    } catch { /* keep the last count */ }
  }

  /** Re-read /status alone — the top bar's "last checked" stat follows the poller's automatic runs. A failed
   *  read keeps the last status. */
  async function refreshStatus(): Promise<void> {
    try {
      status.value = await json<PoStatus>('/status')
    } catch { /* keep the last status */ }
  }

  async function uploadFiles(files: File[]): Promise<void> {
    uploading.value = true
    error.value = null
    try {
      for (const file of files) {
        const dataBase64 = await fileToBase64(file)
        const c = await json<Confirmation>('/', {
          method: 'POST',
          body: JSON.stringify({ filename: file.name, dataBase64 }),
        })
        logConfirmation(c, 'upload')
        upsert(c)
      }
    } catch (e: any) {
      error.value = e.message
    } finally {
      uploading.value = false
    }
  }

  async function recheck(id: string): Promise<void> {
    error.value = null
    try {
      const c = await json<Confirmation>(`/${id}/recheck`, { method: 'POST' })
      logConfirmation(c, 'recheck')
      upsert(c)
    } catch (e: any) {
      error.value = e.message
    }
  }

  async function apply(id: string, releaseUpdates: ReleaseUpdate[]): Promise<{ ok: boolean }> {
    applying.value = true
    error.value = null
    try {
      upsert(await json<Confirmation>(`/${id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ releaseUpdates }),
      }))
      return { ok: true }
    } catch (e: any) {
      error.value = e.message
      return { ok: false }
    } finally {
      applying.value = false
    }
  }

  /** Check the PO mailbox now instead of waiting for the poll interval. */
  const polling = ref(false)
  async function pollMail() {
    polling.value = true
    error.value = null
    try {
      const summary = await json<{ fetched: number; ingested: number; skipped: number; ignored?: number; failed: number; backfilled?: number; errors: string[] }>(
        '/mail/poll', { method: 'POST' }
      )
      // Not load(): once the page has loaded it returns without fetching, so the new rows and the new
      // "last checked" never showed until the next open.
      await Promise.all([refreshQuiet(), refreshStatus()])
      return summary
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      return null
    } finally {
      polling.value = false
    }
  }

  return {
    polling,
    pollMail,
    fetchEmailPreview,
    fetchPdfObjectUrl,
    confirmations, status, loading, uploading, applying, error,
    load, refreshQuiet, refreshStatus, uploadFiles, recheck, apply,
  }
}
